import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function TimesheetsPanel() {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ employee_id: "", date: todayStr(), in: "", out: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, in, out}

  const load = async () => {
    try {
      const [e, emps] = await Promise.all([
        api.get("/admin/time-entries"),
        api.get("/admin/employees"),
      ]);
      setEntries(e.data);
      setEmployees(emps.data);
    } catch (err) {
      toast.error(apiError(err));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const addShift = async () => {
    if (!addForm.employee_id || !addForm.date || !addForm.in) {
      toast.error("Employee, date and clock-in time are required");
      return;
    }
    try {
      await api.post("/admin/time-entries", {
        employee_id: addForm.employee_id,
        clock_in: fromLocalInput(`${addForm.date}T${addForm.in}`),
        clock_out: addForm.out ? fromLocalInput(`${addForm.date}T${addForm.out}`) : null,
      });
      toast.success("Shift added");
      setAddForm({ employee_id: "", date: todayStr(), in: "", out: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/time-entries/${editing.id}`, {
        clock_in: fromLocalInput(editing.in),
        clock_out: fromLocalInput(editing.out),
      });
      toast.success("Shift updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this time entry?")) return;
    await api.delete(`/admin/time-entries/${id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  const weekTotals = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const totals = {};
    for (const e of entries) {
      if (e.hours != null && new Date(e.clock_in) >= monday) {
        totals[e.employee_name] = (totals[e.employee_name] || 0) + e.hours;
      }
    }
    return totals;
  }, [entries]);

  if (loading) return <p className="py-16 text-center text-chalk/50">Loading timesheets…</p>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-chalk/50">Edit any shift, or add one manually when someone forgets to clock in</p>
        <button
          data-testid="timesheet-add-toggle"
          onClick={() => setAddOpen((o) => !o)}
          className="flex items-center gap-1.5 rounded-full bg-artichoke px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-deep"
        >
          <Plus className="h-4 w-4" /> Add Shift
        </button>
      </div>

      {addOpen && (
        <div data-testid="timesheet-add-form" className="mt-4 rounded-3xl border-2 border-artichoke/30 bg-cream p-5">
          <h3 className="font-display text-base font-semibold text-deep">Manual shift entry</h3>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs font-bold text-chalk/50">
              Employee
              <select
                data-testid="timesheet-add-employee"
                value={addForm.employee_id}
                onChange={(e) => setAddForm({ ...addForm, employee_id: e.target.value })}
                className="mt-1 block w-40 rounded-2xl border-2 border-chalk/10 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              >
                <option value="">Choose…</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-chalk/50">
              Date
              <input
                data-testid="timesheet-add-date"
                type="date"
                value={addForm.date}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                className="mt-1 block rounded-2xl border-2 border-chalk/10 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              />
            </label>
            <label className="text-xs font-bold text-chalk/50">
              Clock in
              <input
                data-testid="timesheet-add-in"
                type="time"
                value={addForm.in}
                onChange={(e) => setAddForm({ ...addForm, in: e.target.value })}
                className="mt-1 block rounded-2xl border-2 border-chalk/10 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              />
            </label>
            <label className="text-xs font-bold text-chalk/50">
              Clock out (optional)
              <input
                data-testid="timesheet-add-out"
                type="time"
                value={addForm.out}
                onChange={(e) => setAddForm({ ...addForm, out: e.target.value })}
                className="mt-1 block rounded-2xl border-2 border-chalk/10 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              />
            </label>
            <button
              data-testid="timesheet-add-save"
              onClick={addShift}
              className="rounded-full bg-artichoke px-6 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-deep"
            >
              Save Shift
            </button>
          </div>
        </div>
      )}

      {Object.keys(weekTotals).length > 0 && (
        <div className="mb-6 mt-6 flex flex-wrap gap-3">
          {Object.entries(weekTotals).map(([name, hours]) => (
            <div key={name} data-testid={`week-total-${name}`} className="rounded-2xl bg-artichoke px-5 py-3 text-cream">
              <p className="text-xs font-bold uppercase tracking-wider text-cream/70">{name}</p>
              <p className="font-display text-xl font-bold">{hours.toFixed(1)} hrs this week</p>
            </div>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <p data-testid="timesheets-empty" className="py-16 text-center text-sm text-chalk/50">
          No shifts yet. Once the team starts clocking in at /clock, their hours show up here.
        </p>
      ) : (
        <div className="mt-6 overflow-hidden rounded-3xl border border-leafline bg-cream">
          <table className="w-full text-sm" data-testid="timesheets-table">
            <thead>
              <tr className="border-b border-leafline bg-sage/60 text-left text-xs font-bold uppercase tracking-wider text-chalk/50">
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">In</th>
                <th className="px-5 py-3">Out</th>
                <th className="px-5 py-3 text-right">Hours</th>
                <th className="px-5 py-3 text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} data-testid={`time-entry-${e.id}`} className="border-b border-chalk/5 last:border-0">
                  <td className="px-5 py-3 font-bold">{e.employee_name}</td>
                  <td className="px-5 py-3 text-chalk/60">{fmtDate(e.clock_in)}</td>
                  {editing?.id === e.id ? (
                    <>
                      <td className="px-5 py-3" colSpan={2}>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            data-testid={`time-edit-in-${e.id}`}
                            type="datetime-local"
                            value={editing.in}
                            onChange={(ev) => setEditing({ ...editing, in: ev.target.value })}
                            className="rounded-xl border-2 border-artichoke bg-white px-2 py-1.5 text-xs font-medium outline-none"
                          />
                          <span className="text-chalk/40">→</span>
                          <input
                            data-testid={`time-edit-out-${e.id}`}
                            type="datetime-local"
                            value={editing.out}
                            onChange={(ev) => setEditing({ ...editing, out: ev.target.value })}
                            className="rounded-xl border-2 border-artichoke bg-white px-2 py-1.5 text-xs font-medium outline-none"
                          />
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right">—</td>
                      <td className="px-5 py-3 text-right">
                        <button data-testid={`time-edit-save-${e.id}`} onClick={saveEdit} className="mr-2 rounded-full bg-artichoke px-4 py-1.5 text-xs font-bold text-cream">
                          Save
                        </button>
                        <button onClick={() => setEditing(null)} className="text-xs font-bold text-chalk/50">
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 text-artichoke">{fmtTime(e.clock_in)}</td>
                      <td className="px-5 py-3 text-grape">
                        {e.clock_out ? fmtTime(e.clock_out) : "— still in"}
                      </td>
                      <td className="px-5 py-3 text-right font-editorial font-semibold text-crispy">
                        {e.hours != null ? e.hours.toFixed(2) : "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          data-testid={`time-edit-${e.id}`}
                          onClick={() => setEditing({ id: e.id, in: toLocalInput(e.clock_in), out: toLocalInput(e.clock_out) })}
                          className="mr-1 rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-artichoke hover:text-artichoke"
                          aria-label="Edit entry"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`time-delete-${e.id}`}
                          onClick={() => remove(e.id)}
                          className="rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-red-300 hover:text-red-600"
                          aria-label="Delete entry"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
